import { NavLink, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '../lib/auth-context';

export interface NavItem {
  to: string;
  label: string;
  icon: string;
  badge?: number;
  /** Small status dot (no count) — e.g. KYC "in review" on /portal/compliance. */
  dot?: boolean;
  end?: boolean;
}

interface Props {
  /** Display name (client = company name; operator = staff full name). */
  who: string;
  /** Subtitle under display name (client = "Body Corporate · UK"; operator = "Engagement Lead · London"). */
  whoSub: string;
  /** Initials for the avatar circle. */
  initials: string;
  /** Sidebar nav items. */
  items: NavItem[];
  /** Footer block — engagement lead / role select / etc. */
  footer?: ReactNode;
  /** Where to send the user after sign-out. */
  signOutTo: string;
  children: ReactNode;
  /** Avatar background colour override (operator portal uses a darker tone). */
  avatarBg?: string;
}

export function PortalShell({ who, whoSub, initials, items, footer, signOutTo, children, avatarBg }: Props) {
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate(signOutTo, { replace: true });
  };

  return (
    <div className="portal-shell">
      <aside className="portal-side" style={{ display: 'flex', flexDirection: 'column' }}>
        <div className="who">
          <div className="av" style={avatarBg ? { background: avatarBg, color: '#fff' } : undefined}>{initials}</div>
          <div>
            <div className="nm">{who}</div>
            <div className="co">{whoSub}</div>
          </div>
        </div>

        <nav>
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) => (isActive ? 'on' : '')}
              style={{ textDecoration: 'none' }}
            >
              {({ isActive }) => (
                <button
                  className={`${isActive ? 'on' : ''}${it.badge || it.dot ? ' has-badge' : ''}`}
                  type="button"
                  style={{ width: '100%' }}
                  tabIndex={-1}
                >
                  <span style={{ color: 'var(--fg-mute)', fontFamily: 'var(--mono)', fontSize: 13, textAlign: 'center' }}>{it.icon}</span>
                  <span>{it.label}</span>
                  {it.badge ? <span className="badge">{it.badge}</span>
                    : it.dot ? <span className="badge badge-dot" aria-label="Update" /> : null}
                </button>
              )}
            </NavLink>
          ))}
        </nav>

        <div style={{ marginTop: 'auto', paddingTop: 24, fontSize: 11, color: 'var(--fg-mute)', lineHeight: 1.6, borderTop: '1px solid var(--rule)', marginInline: 8 }}>
          {footer}
          <button
            onClick={handleSignOut}
            style={{ background: 'transparent', border: 0, color: 'var(--fg-mute)', padding: '14px 0 0', fontSize: 12, textDecoration: 'underline', cursor: 'pointer' }}
          >
            Sign out →
          </button>
        </div>
      </aside>

      <section className="portal-main">{children}</section>
    </div>
  );
}
